<template>
	<Modal :id="modalId">
		<template #title>{{ trans('rotations.requests.accept.title') }}</template>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-2">
			<LabeledObject full class="md:col-span-2">
				<template #label>{{ trans('rotations.requests.online') }}</template>
				<p>{{ request.online ? trans('general.yes') : trans('general.no') }}</p>
			</LabeledObject>
			<LabeledObject full>
				<template #label>{{ trans('rotations.requests.election_begins_at') }}</template>
				<p>{{ dayjs(request.election_begins_at).format(trans('general.dayjs.datetime')) }}</p>
			</LabeledObject>
			<LabeledObject full>
				<template #label>{{ trans('rotations.requests.election_ends_at') }}</template>
				<p>{{ dayjs(request.election_ends_at).format(trans('general.dayjs.datetime')) }}</p>
			</LabeledObject>
			<LabeledObject full>
				<template #label>{{ trans('rotations.requests.begins_at') }}</template>
				<IconDateTimeInput v-model="newRotation.begins_at" :min="min" icon-class="fa-solid fa-hourglass-start"/>
			</LabeledObject>
			<LabeledObject full>
				<template #label>{{ trans('rotations.requests.ends_at') }}</template>
				<IconDateTimeInput v-model="newRotation.ends_at" :min="newRotation.begins_at" :max="dayjs(request.election_begins_at).format('YYYY-MM-DD HH:mm')" icon-class="fa-solid fa-hourglass-end"/>
			</LabeledObject>
		</div>
		<template #footer>
			<PrimaryButton :color="Color.Green" @click.stop="accept">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.confirm') }}
			</PrimaryButton>
		</template>
	</Modal>
	<Card :title="request.college.acronym" :subtitle="request.college.name">
		<template #badges>
			<Badge :color="badgeColor" active>{{ trans(`admin.periods.requests.status.${request.status}`) }}</Badge>
			<i v-if="request.online" class="fa-solid fa-video-camera text-gray-400"/>
		</template>
		<LabeledObject>
			<template #label>{{ trans('rotations.requests.election_begins_at') }}</template>
			<p>{{ dayjs(request.election_begins_at).format(trans('general.dayjs.datetime')) }}</p>
		</LabeledObject>
		<LabeledObject>
			<template #label>{{ trans('rotations.requests.election_ends_at') }}</template>
			<p>{{ dayjs(request.election_ends_at).format(trans('general.dayjs.datetime')) }}</p>
		</LabeledObject>
		<LabeledObject v-if="request.user">
			<template #label>{{ trans('admin.users.representative') }}</template>
			<p>{{ request.user.full_name }}</p>
		</LabeledObject>
		<template v-if="request.is_pending" #footer>
			<PrimaryButton :color="Color.Green" @click.stop="showAcceptanceForm">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('admin.periods.requests.actions.accept') }}
			</PrimaryButton>
			<PrimaryButton :color="Color.Red" @click.stop="reject">
				<i class="fa-solid fa-ban mr-2"/>
				{{ trans('admin.periods.requests.actions.reject') }}
			</PrimaryButton>
			<PrimaryButton :color="Color.Purple" @click.stop="reschedule">
				<i class="fa-solid fa-clock-rotate-left mr-2"/>
				{{ trans('admin.periods.requests.actions.reschedule') }}
			</PrimaryButton>
		</template>
	</Card>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {getPeriodRequestStatusColor} from '@/Helpers/period-requests'
import {computed, type ComputedRef, defineProps, type Ref, ref} from 'vue'
import IconDateTimeInput from '@/Components/UI/Inputs/IconDateTimeInput.vue'
import {type RotationRequest} from '@/Types/Periods/Rotations/RotationRequest'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	request:RotationRequest,
}>()

const updating:Ref<boolean> = ref(false)
const rejecting:Ref<boolean> = ref(false)
const rescheduling:Ref<boolean> = ref(false)
const min:Ref<string> = ref(dayjs().format('YYYY-MM-DD'))
const request:Ref<RotationRequest> = ref(props.request)
const newRotation:Ref<Rotation> = ref({
	begins_at: '',
	ends_at: '',
} as Rotation)
const modalId:Ref<string> = ref(`accept-request-${props.request.id}-modal`)

const badgeColor:ComputedRef<Color> = computed(():Color => {
	return getPeriodRequestStatusColor(request.value)
})

function showAcceptanceForm():void{
	newRotation.value.begins_at = min.value
	newRotation.value.ends_at = min.value
	showModal(modalId.value)
}

function accept():void{
	if(!updating.value){
		updating.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.requests.update', request.value.id), {
			begins_at: newRotation.value.begins_at,
			ends_at: newRotation.value.ends_at,
			action: 'accept',
		}).then(({data}) => {
			showSuccess(trans('general.updated'))
			request.value = data
			closeModal(modalId.value)
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}

function reschedule():void{
	if(!rescheduling.value && confirm(trans('general.confirm_action'))){
		rescheduling.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.requests.update', request.value.id), {
			action: 'reschedule',
		}).then(({data}) => {
			showSuccess(trans('general.updated'))
			request.value = data
		}).catch(showValidationErrors).finally(() => {
			rescheduling.value = false
		})
	}
}

function reject():void{
	if(!rejecting.value && confirm(trans('general.confirm_action'))){
		rejecting.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.requests.update', request.value.id), {
			action: 'reject',
		}).then(({data}) => {
			showSuccess(trans('general.updated'))
			request.value = data
		}).catch(showValidationErrors).finally(() => {
			rejecting.value = false
		})
	}
}
</script>